import moment from 'moment';

import type { ChartDataPoint, SystemEnergyHistoryDataItem } from '../types/charts';

/**
 * Raw data structure from the API for historic system data
 */
interface HistoricSystemDataRecord {
  start_date: number; // Unix timestamp in seconds
  end_date: number; // Unix timestamp in seconds
  battery_soc: string | number;
  solar_generation: number;
  load_consumption: number;
  imported: number;
  exported: number;
}

/**
 * Processes the data for the battery state of charge chart
 *
 * @param data - Array of historic system data records from the API
 * @returns Array of chart data points with time (x) and battery state of charge (y)
 */
export const processSocChartData = (data: HistoricSystemDataRecord[]): ChartDataPoint[] =>
  data.map((record) => ({
    x: moment(record.start_date * 1000).format('HH:mm'),
    y: parseInt(String(record.battery_soc), 10),
  }));

/**
 * Processes the data for the system energy history chart
 *
 * @param data - Array of historic system data records from the API
 * @returns Array of processed data with time and energy values in kWh
 */
export const processSystemEnergyHistoryChartData = (data: HistoricSystemDataRecord[]): SystemEnergyHistoryDataItem[] =>
  data.map((record) => ({
    time: moment
      .utc(record.start_date * 1000)
      .local()
      .format('HH:mm'),
    'Solar generation': parseFloat(String(record.solar_generation / 1000)),
    Consumption: parseFloat(String(record.load_consumption / 1000)),
    Imported: parseFloat(String(record.imported / 1000)),
    Exported: -parseFloat(String(record.exported / 1000)),
  }));

/**
 * Calculates the y domain for the system energy history chart
 *
 * @param processedData - Array of processed system energy history data
 * @param setYDomain - State setter function for the y domain
 */
export const calculateYDomain = (
  processedData: SystemEnergyHistoryDataItem[],
  setYDomain: (domain: [number, number]) => void
): void => {
  let minY = 0;
  let maxY = 0;

  processedData.forEach((item) => {
    const sum = item['Solar generation'] + item.Consumption + item.Imported + item.Exported;
    minY = Math.min(minY, sum, item.Exported);
    maxY = Math.max(maxY, sum, item['Solar generation'], item.Consumption, item.Imported);
  });

  setYDomain([minY * 1.1, maxY * 1.1]);
};
